import { useEffect, useMemo, useState } from 'react'
import { getDaysSinceColorClass } from 'utils/formatting'
import { format, differenceInDays } from 'date-fns'
import {
  PiCalendarBlank,
  PiCaretDown,
  PiCalendarStar,
  PiHandshake,
  PiEnvelopeOpen,
  PiNotepad,
  PiHandCoins,
  PiStack,
  PiListBullets,
  PiFileText,
  PiX,
} from 'react-icons/pi'
import { CompanyPageAccordion } from '../CompanyPageAccordion'
import { ResponsiveTabs, ResponsiveTabConfig } from '../../Tabs'
import { EventHistoryList } from './EventHistoryList'
import { Popover, PopoverContent, PopoverTrigger } from '../../UI/popover'
import { Calendar } from '../../UI/calendar'
import { SampleRequestPreviewModal } from '../../Samples/SampleRequestPreviewModal'
import { EventHistoryFilterModal } from './EventHistoryFilterModal'
import { EventHistoryFilterChips } from './EventHistoryFilterChips'
import { EmailsModal } from '../Modals/EmailsModal/EmailsModal'
import { ActivityV2 } from 'models/activityV2'
import { EmailMessage } from 'models/emailIntegration'
import { useEventHistoryFilterStore } from './EventHistoryFilterStore'
import { useEventHistoryQuery } from './useEventHistoryQuery'
import { buildEmailFromEvent } from './utils'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export type EventHistoryTab =
  | 'all'
  | 'deals'
  | 'notes'
  | 'reminders'
  | 'samples'
  | 'emails'
  | 'documents'
  | 'account_attributes'

const EVENT_HISTORY_TABS: ResponsiveTabConfig<EventHistoryTab>[] = [
  { key: 'all', title: 'All', icon: PiListBullets },
  { key: 'deals', title: 'Deals', icon: PiHandshake },
  { key: 'notes', title: 'Notes', icon: PiNotepad },
  { key: 'reminders', title: 'Reminders', icon: PiCalendarStar },
  { key: 'samples', title: 'Samples', icon: PiHandCoins },
  { key: 'emails', title: 'Emails', icon: PiEnvelopeOpen },
  { key: 'documents', title: 'Documents', icon: PiFileText },
  { key: 'account_attributes', title: 'Account Attributes', icon: PiStack },
]

interface EventHistoryAccordionProps {
  chainProxyId: number
  doorProxyId?: number
  companyType?: string
}

export function EventHistoryAccordion({
  chainProxyId,
  doorProxyId,
}: EventHistoryAccordionProps) {
  const { dealDetailPage } = useFeatureFlagService()
  const [selectedTab, setSelectedTab] = useState<EventHistoryTab>('all')
  const [dateRange, setDateRange] = useState<
    | {
        from: Date
        to?: Date
      }
    | undefined
  >(undefined)
  // Controlled popover so the "Apply" button can close it explicitly.
  // Selections still auto-apply via setDateRange — Apply just dismisses.
  const [datePickerOpen, setDatePickerOpen] = useState(false)

  // Reset shared filter store on mount to prevent cross-page filter leakage
  useEffect(() => {
    useEventHistoryFilterStore.getState().clearAllFilters()
  }, [chainProxyId])

  // Shared query hook — replaces inline useInfiniteQuery + buildQueryParams
  const {
    groupedEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useEventHistoryQuery({
    chainProxyId,
    doorProxyId,
    selectedTab,
    dateRange,
  })

  // Sample preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewingSampleId, setPreviewingSampleId] = useState<number | null>(
    null
  )

  const handlePreviewRequest = (sampleId: number) => {
    setPreviewingSampleId(sampleId)
    setShowPreviewModal(true)
  }

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setPreviewingSampleId(null)
  }

  // Email view modal state
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null)

  const handleEmailClick = (event: ActivityV2) => {
    setSelectedEmail(buildEmailFromEvent(event))
  }

  // Header: most recent event + days badge
  const mostRecentEvent = useMemo(() => {
    if (groupedEvents.length === 0) return null
    return groupedEvents[0]?.events?.[0] ?? null
  }, [groupedEvents])

  const daysSinceUpdate = useMemo(() => {
    if (!mostRecentEvent?.created_at) return 0
    return differenceInDays(new Date(), new Date(mostRecentEvent.created_at))
  }, [mostRecentEvent])

  const headerRight = mostRecentEvent ? (
    <>
      <div className="flex items-center gap-1.5">
        <PiCalendarBlank className="w-5 h-5 text-fg-secondary" />
        <span
          className="text-sm font-light text-fg tabular-nums"
          style={{ lineHeight: '100%' }}
        >
          Updated {format(new Date(mostRecentEvent.created_at), 'MM/dd/yyyy')}
        </span>
      </div>

      <div className="flex h-6 items-center gap-1.5 rounded-full border border-line bg-surface-raised px-2.5 py-0.5">
        <div
          className={`h-2 w-2 rounded-full ${getDaysSinceColorClass(daysSinceUpdate)}`}
        />
        <span
          className="text-sm font-light text-fg tabular-nums"
          style={{ lineHeight: '100%' }}
        >
          <span className="font-semibold">{daysSinceUpdate}</span>{' '}
          {daysSinceUpdate === 1 ? 'day ago' : 'days ago'}
        </span>
      </div>
    </>
  ) : null

  return (
    <StageColorProvider value={dealDetailPage ? 'pipeline' : 'win_rate'}>
      <CompanyPageAccordion
        title="Event History"
        defaultOpen={true}
        stickyHeader={true}
        headerRight={headerRight}
        noPaddingBottom={true}
      >
        {/* Tabs and Search - static header */}
        <div className="bg-white space-y-4 pb-1 -mt-2">
          {/* Tabs */}
          <ResponsiveTabs
            tabs={EVENT_HISTORY_TABS}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            variant="pills"
          />

          {/* Filters Row */}
          <div className="w-full flex gap-2">
            {/* Date Range Filter */}
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button className="flex-1 flex items-center gap-2 px-3.5 py-2 bg-white border border-line rounded-md text-base font-medium text-fg-secondary hover:shadow-[0px_1px_4px_rgba(12,12,13,0.05)] transition-shadow">
                  <PiCalendarBlank className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 text-left">
                    {dateRange?.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, 'd MMM yyyy')} -{' '}
                          {format(dateRange.to, 'd MMM yyyy')}
                        </>
                      ) : (
                        format(dateRange.from, 'd MMM yyyy')
                      )
                    ) : (
                      'All time'
                    )}
                  </span>
                  {dateRange?.from ? (
                    // Inline clear affordance — uses span (not button)
                    // to avoid nesting a button inside the popover
                    // trigger button. stopPropagation prevents the
                    // popover from opening when clearing.
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label="Clear date range"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDateRange(undefined)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          setDateRange(undefined)
                        }
                      }}
                      className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-fg-secondary hover:bg-gray-100 hover:text-fg transition-colors"
                    >
                      <PiX className="w-4 h-4" />
                    </span>
                  ) : (
                    <PiCaretDown className="w-5 h-5 flex-shrink-0" />
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={(newRange) => {
                    if (!newRange?.from) {
                      return
                    }
                    setDateRange(newRange as { from: Date; to?: Date })
                  }}
                  numberOfMonths={2}
                  classNames={{
                    day: 'inline-flex items-center justify-center text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 h-9 w-9 p-0 text-fg aria-selected:opacity-100 hover:bg-surface-hover rounded-full',
                    // day_selected is applied to ALL range days by
                    // react-day-picker (start, end, AND middle), so the
                    // green-circle styling lives on day_range_start /
                    // day_range_end instead — middle days stay readable.
                    day_selected: 'rounded-full',
                    day_today: 'font-semibold text-fg',
                    day_range_start:
                      'day-range-start bg-brand-active text-white rounded-full hover:bg-brand-active hover:text-white focus:bg-brand-active focus:text-white',
                    day_range_end:
                      'day-range-end bg-brand-active text-white rounded-full hover:bg-brand-active hover:text-white focus:bg-brand-active focus:text-white',
                    day_range_middle:
                      'day-range-middle aria-selected:bg-transparent aria-selected:text-fg rounded-none hover:bg-transparent',
                    cell: 'h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20 [&:has(.day-range-middle)]:bg-gray-100 [&:has(.day-range-start)]:bg-gradient-to-r [&:has(.day-range-start)]:from-transparent [&:has(.day-range-start)]:from-50% [&:has(.day-range-start)]:to-gray-100 [&:has(.day-range-start)]:to-50% [&:has(.day-range-end)]:bg-gradient-to-l [&:has(.day-range-end)]:from-transparent [&:has(.day-range-end)]:from-50% [&:has(.day-range-end)]:to-gray-100 [&:has(.day-range-end)]:to-50%',
                    head_cell: 'text-fg-secondary w-9 font-normal text-xs pb-2',
                  }}
                />
                <div className="flex items-center justify-between gap-2 p-3 border-t">
                  {/* Clear sits on the left, Apply on the right. Clear
                      disables when nothing is selected so Apply stays
                      anchored to the right edge. */}
                  <button
                    type="button"
                    onClick={() => setDateRange(undefined)}
                    disabled={!dateRange}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Clear dates
                  </button>
                  <button
                    type="button"
                    onClick={() => setDatePickerOpen(false)}
                    className="inline-flex items-center justify-center px-4 py-1.5 rounded-md bg-brand-hover text-white text-sm font-medium hover:bg-brand-dark transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </PopoverContent>
            </Popover>

            {/* More Filters Button */}
            <EventHistoryFilterModal selectedTab={selectedTab} />
          </div>

          {/* Filter Chips */}
          <EventHistoryFilterChips selectedTab={selectedTab} />
        </div>

        <EventHistoryList
          isLoading={isLoading}
          error={error}
          groupedEvents={groupedEvents}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onPreviewRequest={handlePreviewRequest}
          onEmailClick={handleEmailClick}
        />

        {/* Sample Preview Modal */}
        {showPreviewModal && (
          <SampleRequestPreviewModal
            show={showPreviewModal}
            onHide={handleClosePreviewModal}
            sampleRequestId={previewingSampleId}
          />
        )}

        {/* Email View Modal */}
        {selectedEmail && (
          <EmailsModal
            show={!!selectedEmail}
            onHide={() => setSelectedEmail(null)}
            emailOnlyMode
            initialEmail={selectedEmail}
          />
        )}
      </CompanyPageAccordion>
    </StageColorProvider>
  )
}
